import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  ActionsDialogue, ActionsLigne, BarreOutils, Bouton, Carte, Champ, Dialogue,
  EnTetePage, Etiquette, GroupeBoutons,
  IconeCrayon, IconePlus, Message, Selecteur, Tableau, Vide,
  useRetours,
} from '@journal/ui';
import { DialogueReprise } from '../components/DialogueReprise.tsx';
import { ReglagesGeneral } from '../components/ReglagesGeneral.tsx';
import { ReglagesMatieres } from '../components/ReglagesMatieres.tsx';
import { ReglagesSuppressions } from '../components/ReglagesSuppressions.tsx';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import { messageErreur, useSession } from '../lib/session.tsx';
import { useAnneeActive, type AnneeScolaire } from '../lib/annee.tsx';
import { decalerJour as decalerJourIso } from './Journal.tsx';
import { lundiDe } from './Progressions.tsx';

/**
 * Réglages : années scolaires, périodes, matières.
 *
 * ── Une seule année active à la fois ────────────────────────────────────────
 * La base l'impose par un index partiel unique, et le serveur bascule les deux
 * lignes dans une même transaction. L'interface ne propose donc pas de
 * « décocher » une année : on en active une autre, ce qui désactive la
 * précédente. Offrir les deux gestes laisserait croire qu'un état sans année
 * active est normal — il ne l'est pas, la moitié de l'application cesse alors
 * de fonctionner.
 */

export interface Periode {
  id: string;
  schoolYearId: string;
  name: string;
  number: number;
  startsOn: string;
  endsOn: string;
}

export interface SousDomaine {
  id: string;
  name: string;
  position: number;
}

export interface Matiere {
  id: string;
  name: string;
  abbreviation: string;
  color: string;
  position: number;
  areas: SousDomaine[];
}

const dateFr = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR');

/**
 * Semaines d'une période — la liste personnalisée si elle existe, le calcul
 * sinon.
 *
 * ── Pourquoi une liste modifiable ───────────────────────────────────────────
 * Le calcul automatique enchaîne les lundis entre les deux bornes. C'est juste
 * neuf fois sur dix, et faux le reste du temps : une semaine banalisée, un
 * stage, une rentrée décalée d'un jour. Sans possibilité de corriger, toute la
 * progression se décale d'un cran et le report tombe sur la mauvaise semaine.
 *
 * Reprend `getWeeksInPeriode` et le réglage `semaines_perso` de l'application
 * d'origine.
 */
export function semainesDePeriode(
  periode: { id: string; startsOn: string; endsOn: string },
  perso: Record<string, string[]> | null,
): string[] {
  const surMesure = perso?.[periode.id];
  /*
   * ── La liste ne vaut que si elle RECOUPE encore la période ────────────────
   * Elle l'emporte sur le calcul, c'est tout son intérêt : sans cela on ne
   * pourrait pas retirer une semaine banalisée. Mais une période redatée —
   * changement d'année scolaire, application du calendrier officiel — laisse
   * une liste pointant sur les lundis de l'an dernier. La faire gagner
   * affichait alors une progression entièrement située dans une année révolue,
   * sans qu'un seul message ne l'explique.
   *
   * Le serveur vide désormais ces listes en redatant la période. Cette garde
   * reste, pour les espaces dont le réglage porte encore d'anciennes clés :
   * une donnée déjà écrite ne se corrige pas d'elle-même.
   *
   * Un seul lundi dans les bornes suffit à la tenir pour vivante — une liste
   * qui déborde d'une semaine sur les vacances est un choix, pas une erreur.
   */
  const vivante = surMesure?.some((l) => l >= periode.startsOn && l <= periode.endsOn);
  if (surMesure && surMesure.length > 0 && vivante) return [...surMesure].sort();

  const semaines: string[] = [];
  // Le premier lundi À PARTIR du début : une période qui commence un mercredi
  // a sa première semaine complète le lundi suivant.
  let courant = lundiDe(periode.startsOn);
  if (courant < periode.startsOn) courant = decalerJourIso(courant, 7);
  while (courant <= periode.endsOn) {
    semaines.push(courant);
    courant = decalerJourIso(courant, 7);
  }
  return semaines;
}

/** Une rentrée que le back-office a publiée, proposable à l'école. */
export interface RentreeDisponible {
  year: number;
  label: string;
  startsOn: string;
  endsOn: string;
}

/** Ce que le serveur a fait aux périodes en changeant d'année. */
export interface Alignement {
  creees: number;
  realignees: number;
  semainesLiberees: number;
  /** Édition adoptée, si elle a changé. */
  programmeAdopte: string | null;
  calendrierInconnu: boolean;
}

/**
 * Ce qu'on dit après avoir changé d'année.
 *
 * ── Pourquoi ce n'est pas un simple « Année activée » ───────────────────────
 * Le serveur a pu redater cinq périodes et effacer des listes de semaines
 * saisies à la main. Personne ne l'a demandé explicitement, et c'est
 * exactement le genre d'effet qu'on découvre trois écrans plus loin en croyant
 * à une perte de données. Il se dit ici, au moment où il se produit.
 *
 * Le calendrier inconnu se dit aussi, et c'est le message le plus utile du
 * lot : l'année n'a aucune période, rien ne s'affichera nulle part, et la
 * seule chose à faire est d'aller les créer.
 */
export function messageAlignement(
  libelle: string,
  bilan: Alignement | null,
  /** Ce qu'on vient de faire : la même phrase sert à l'activation et à la mise à jour. */
  action: 'activée' | 'mise à jour' | 'créée' = 'activée',
): string {
  if (!bilan) return `Année ${libelle} ${action}.`;
  if (bilan.calendrierInconnu) {
    return `Année ${libelle} ${action}, le calendrier officiel de cette rentrée `
      + 'n’est pas encore connu : écrivez au support pour que ses périodes soient ajoutées.';
  }

  const faits: string[] = [];
  /*
   * Le programme EN PREMIER quand il change : c'est le fait le plus lourd de
   * conséquences du lot. Il décide des objectifs proposés en programmation et
   * en évaluation à tous les enseignants de l'école — bien davantage que cinq
   * dates de période.
   */
  if (bilan.programmeAdopte) faits.push(`programme « ${bilan.programmeAdopte} »`);
  if (bilan.creees > 0) faits.push(`${bilan.creees} périodes créées`);
  if (bilan.realignees > 0) faits.push(`${bilan.realignees} périodes replacées`);
  if (bilan.semainesLiberees > 0) {
    faits.push(
      bilan.semainesLiberees === 1
        ? '1 liste de semaines sur mesure remise au calcul automatique'
        : `${bilan.semainesLiberees} listes de semaines sur mesure remises au calcul automatique`,
    );
  }
  return faits.length === 0
    ? `Année ${libelle} ${action}.`
    : `Année ${libelle} ${action} · ${faits.join(', ')}.`;
}

/** Lecture tolérante du réglage : un JSON abîmé ne doit pas casser l'écran. */
export function lireSemainesPerso(brut: string | undefined): Record<string, string[]> | null {
  if (!brut) return null;
  try {
    const valeur = JSON.parse(brut) as unknown;
    if (!valeur || typeof valeur !== 'object' || Array.isArray(valeur)) return null;
    return valeur as Record<string, string[]>;
  } catch {
    return null;
  }
}

/** Suggère l'année scolaire en cours : rentrée en septembre. */
export function anneeSuggeree(aujourdhui = new Date()): { label: string; debut: string; fin: string } {
  const mois = aujourdhui.getUTCMonth();
  // Avant août, on est encore dans l'année scolaire commencée l'automne
  // précédent : proposer « 2026-2027 » en mars serait faux.
  const premiere = mois >= 7 ? aujourdhui.getUTCFullYear() : aujourdhui.getUTCFullYear() - 1;
  return {
    label: `${premiere}-${premiere + 1}`,
    debut: `${premiere}-09-01`,
    fin: `${premiere + 1}-07-07`,
  };
}

export function Reglages() {
  const { api, deconnecter } = useSession();
  const naviguer = useNavigate();
  const { notifier } = useRetours();
  const anneeActive = useAnneeActive();
  const annees = useDonnees<AnneeScolaire[]>('/school-years');
  const matieres = useDonnees<Matiere[]>('/subjects');
  const { executer, enCours, erreur } = useEcriture();

  const [anneeOuverte, setAnneeOuverte] = useState(false);
  /** Année en cours de modification. `null` = création. */
  const [anneeEditee, setAnneeEditee] = useState<AnneeScolaire | null>(null);
  /** Rentrée choisie dans le menu déroulant, par année de rentrée. */
  const [rentreeChoisie, setRentreeChoisie] = useState('');




  const reglages = useDonnees<Record<string, string>>('/settings');

  const [sousDomaines, setSousDomaines] = useState<Matiere | null>(null);
  const [nomSousDomaine, setNomSousDomaine] = useState('');

  /** Onglet courant. Cinq sections, comme l'application d'origine. */
  const [onglet, setOnglet] = useState<
    'general' | 'annee' | 'periodes' | 'matieres' | 'suppressions'
  >('general');

  /*
   * Les niveaux se lisent sur la CLASSE, plus sur le réglage d'école.
   *
   * Le réglage subsiste en base pour les espaces qui n'ont pas encore été
   * repris ; il sert de repli, jamais de source.
   */
  const classes = useDonnees<Array<{
    id: string; name: string; levels: string; teacherId: string | null;
  }>>('/classrooms');
  const moi = useDonnees<{ id: string; role: string; tenantName: string }>('/auth/me');
  const classeCourante = classes.donnees
    ?.find((c) => c.teacherId === moi.donnees?.id) ?? null;
  /*
   * ── Les gestes d'administration de l'ÉCOLE, réservés aux responsables ─────
   * Le serveur les refuse déjà (`requireRole('OWNER','ADMIN')`) : redater une
   * année, replacer une période ou la supprimer touche au travail de tous les
   * collègues. Mais l'écran les proposait à n'importe quel compte, et le clic
   * ne rendait qu'« Votre rôle ne permet pas cette action » — un bouton qui
   * échoue toujours est pire que pas de bouton, parce qu'il fait douter du
   * produit plutôt que d'expliquer le partage des droits.
   *
   * CRÉER une période ou une matière reste ouvert, comme côté serveur : cela
   * ne détruit rien. Ouvrir une ANNÉE, en revanche, relève de la direction :
   * c'est à ce moment qu'elle choisit ce qu'on reprend de l'année précédente,
   * et une rentrée ne s'ouvre qu'une fois — un enseignant qui l'ouvrait le
   * premier lui retirait ce choix.
   */
  const peutAdministrer = moi.donnees?.role === 'OWNER' || moi.donnees?.role === 'ADMIN';
  const peutModifierClasse = !!classeCourante && peutAdministrer;

  const niveauxChoisis = (classeCourante?.levels ?? reglages.donnees?.niveaux ?? '')
    .split(',').map((n) => n.trim().toUpperCase()).filter(Boolean);

  /** Écrit les niveaux sur la classe, et non sur un réglage d'école. */
  async function enregistrerNiveaux(niveaux: string[]) {
    if (!classeCourante) return;
    const ok = await executer(() =>
      api.requete(`/classrooms/${classeCourante.id}`, {
        methode: 'PATCH',
        corps: { levels: niveaux.join(',') },
      }));
    if (ok) {
      notifier('Niveaux enregistrés.');
      await classes.recharger();
    }
  }
  const zoneChoisie = (reglages.donnees?.zone ?? 'B').toUpperCase();

  /** Année de rentrée, pour le calendrier des vacances de l'onglet Général. */
  const anneeRentree = (() => {
    const libelleAnnee = anneeActive.donnees?.label ?? '';
    const debutLibelle = libelleAnnee.match(/^(\d{4})/);
    if (debutLibelle?.[1]) return Number(debutLibelle[1]);
    const aujourdhui = new Date();
    return aujourdhui.getUTCMonth() >= 7
      ? aujourdhui.getUTCFullYear() : aujourdhui.getUTCFullYear() - 1;
  })();

  const vacances = useDonnees<{
    zone: string;
    holidays: Array<{ nom: string; debut: string; fin: string }>;
  }>(
    onglet === 'general' ? `/settings/holidays/${anneeRentree}?zone=${zoneChoisie}` : null,
    [onglet, anneeRentree, zoneChoisie],
  );

  /*
   * Les rentrées ouvrables, telles que le back-office les publie.
   *
   * Chargées avec l'onglet et non à l'ouverture du formulaire : la liste sert
   * aussi à savoir s'il y a quelque chose à ouvrir, et un menu déroulant qui
   * se remplit une demi-seconde après son affichage se lit comme un défaut.
   */
  /*
   * TOUTES les rentrées publiées : la modification d'une année doit pouvoir
   * proposer la sienne. La création, elle, écarte celles déjà ouvertes ici.
   */
  const publiees = useDonnees<RentreeDisponible[]>(
    onglet === 'annee' ? '/school-years/available?toutes=true' : null,
    [onglet],
  );
  const dejaOuvertes = new Set((annees.donnees ?? []).map((a) => a.label));
  const ouvrables = (publiees.donnees ?? []).filter((c) => !dejaOuvertes.has(c.label));

  /**
   * Référentiel de la CLASSE, pour l'onglet Matières.
   *
   * Cet onglet annonce « vos niveaux » et renvoie à ceux de la classe : il doit
   * donc lire la même source que les modules. Il désignait auparavant UNE
   * version — « celle qui est marquée, sinon la première », donc le cycle 1
   * quand aucune ne l'est. Une classe cochée sur plusieurs cycles n'y voyait
   * que les trois matières de maternelle, alors que la programmation, elle,
   * les montrait toutes.
   */
  const arbreProgramme = useDonnees<Array<{
    id: string; name: string; subject: string; color: string;
    subdomains: Array<{ id: string; name: string }>;
  }>>(onglet === 'matieres' ? '/curriculum/tree' : null, [onglet]);

  /** Enregistre un réglage et recharge : les autres écrans en dépendent. */
  async function enregistrerReglage(cle: string, valeur: string) {
    const ok = await executer(() =>
      api.requete(`/settings/${cle}`, { methode: 'PUT', corps: { value: valeur } }));
    if (ok) {
      notifier(cle === 'niveaux' ? 'Niveaux enregistrés.' : 'Zone enregistrée.');
      await reglages.recharger();
    }
  }

  /**
   * Couleur d'une matière du référentiel.
   *
   * Passe par `color-by-name` : l'écran désigne la matière par son intitulé,
   * puisqu'il travaille sur le programme et non sur la table du client.
   */
  async function changerCouleurMatiere(nom: string, couleur: string) {
    const ok = await executer(() =>
      api.requete('/subjects/color-by-name', {
        methode: 'PUT',
        corps: { name: nom, color: couleur },
      }));
    if (ok) await matieres.recharger();
  }

  /** Remise à zéro des données de l'école. */
  async function exporterEspace() {
    // Passe par le client d'API : le jeton ne vit qu'en mémoire, et une
    // navigation du navigateur ne l'emporterait pas — c'est ce qui faisait
    // répondre 401 au bouton d'export.
    // Un seul CSV de tous les modules ; le serveur décide de la portée selon
    // le rôle — toute l'école pour la direction, ses données pour un enseignant.
    await executer(() => api.telecharger('/rgpd/export.csv', 'export-journal-de-classe.csv'));
  }

  /**
   * Demande la suppression de l'espace, avec le nom de l'école retapé.
   *
   * Pas de seconde boîte de confirmation : retaper le nom EST la confirmation,
   * et le serveur la vérifie. La page est rechargée ensuite : les sessions sont
   * fermées côté serveur, et c'est en se reconnectant qu'on retrouve l'écran
   * qui permet d'annuler.
   */
  /** Rend le message d'erreur à afficher dans la fenêtre, ou `null` une fois supprimé. */
  async function toutSupprimer(motDePasse: string): Promise<string | null> {
    let purgeAfter = '';
    try {
      const r = await api.requete<{ purgeAfter: string }>('/rgpd/deletion', {
        methode: 'POST', corps: { motDePasse },
      });
      purgeAfter = r.purgeAfter;
    } catch (e) {
      return messageErreur(e);
    }
    /*
     * Les sessions sont fermées côté serveur : on se déconnecte ici aussi, et
     * l'on montre la page qui dit ce qui vient de se passer — et comment
     * revenir.
     */
    await deconnecter();
    naviguer(`/espace-supprime?le=${encodeURIComponent(purgeAfter)}`, { replace: true });
    return null;
  }


  const semainesPerso = lireSemainesPerso(reglages.donnees?.semaines_perso);

  const idAnneeActive = anneeActive.donnees?.id ?? null;
  const periodes = useDonnees<Periode[]>(
    idAnneeActive ? `/terms?schoolYearId=${idAnneeActive}` : null,
    [idAnneeActive],
  );

  /** Ouvre le formulaire d'année : en création si `annee` est nul. */
  function ouvrirAnnee(annee: AnneeScolaire | null) {
    setAnneeEditee(annee);
    /*
     * En modification, la rentrée de l'année est présélectionnée : c'est celle
     * dont on relira les dates. En création, on part de rien.
     */
    const sienne = annee
      ? (publiees.donnees ?? []).find((c) => c.label === annee.label)
      : undefined;
    setRentreeChoisie(sienne ? String(sienne.year) : '');
    setAnneeOuverte(true);
  }

  /**
   * Enregistre une année — création ou mise à jour.
   *
   * ── Pourquoi la modification manquait, et ce qu'elle change ───────────────
   * On ne pouvait que CRÉER une année et basculer dessus. Corriger une date de
   * rentrée saisie de travers, ou renommer « 2026-2027 » qu'on avait tapé
   * « 2026-2028 », supposait d'en créer une seconde et d'y tout reprendre.
   *
   * La mise à jour REPLIQUE dans les périodes : celles qui tombent hors des
   * nouvelles bornes sont replacées sur le calendrier officiel de la rentrée,
   * et les listes de semaines sur mesure devenues caduques sont retirées. Sans
   * cela l'année changeait seule, et les cinq périodes restaient un an en
   * arrière — plus rien ne s'affichait, sans le moindre message.
   */
  /*
   * ── Reprendre l'année précédente ───────────────────────────────────────────
   * Proposé à la direction juste après la création d'une année, quand une
   * année antérieure existe : c'est le moment où la question se pose. Le
   * bouton « Reprendre… » de chaque ligne permet d'y revenir plus tard.
   */
  const [repriseVers, setRepriseVers] = useState<{ id: string; label: string } | null>(null);
  const aUneAnneeAvant = (a: { startsOn: string }) =>
    (annees.donnees ?? []).some((autre) => autre.startsOn < a.startsOn);

  async function enregistrerAnnee(evenement: FormEvent) {
    evenement.preventDefault();
    const cible = anneeEditee;
    const rentree = (publiees.donnees ?? []).find((c) => String(c.year) === rentreeChoisie);
    if (!rentree) return;
    // La rentrée seule : le serveur lit libellé et dates au calendrier officiel.
    const corps = { year: rentree.year };
    const libelle = rentree.label;

    let bilan: Alignement | null = null;
    let creee: AnneeScolaire | null = null;
    const ok = await executer(async () => {
      const maj = await api.requete<AnneeScolaire & { alignement: Alignement }>(
        cible ? `/school-years/${cible.id}` : '/school-years',
        { methode: cible ? 'PATCH' : 'POST', corps },
      );
      bilan = maj.alignement;
      if (!cible) creee = maj;
    });
    if (!ok) return;

    setAnneeOuverte(false);
    setAnneeEditee(null);
    /*
     * Quatre rechargements en modification, comme à l'activation : les
     * périodes ont pu être replacées et les semaines sur mesure retirées. Sans
     * eux l'écran continuait d'afficher l'état d'avant, et l'on rouvrait la
     * page en se demandant si le bouton avait fait quelque chose.
     */
    await Promise.all([
      annees.recharger(), anneeActive.recharger(),
      periodes.recharger(), reglages.recharger(),
      // La liste des rentrées ouvrables écarte celles déjà enregistrées :
      // sans ce rechargement, le menu proposerait encore celle qu'on vient
      // de créer, et un second enregistrement produirait un doublon.
      publiees.recharger(),
    ]);
    notifier(messageAlignement(libelle, bilan, cible ? 'mise à jour' : 'créée'));
    const nouvelle = creee as AnneeScolaire | null;
    if (nouvelle && peutAdministrer && aUneAnneeAvant(nouvelle)) {
      setRepriseVers({ id: nouvelle.id, label: nouvelle.label });
    }
  }

  async function activer(annee: AnneeScolaire) {
    let bilan: Alignement | null = null;
    const ok = await executer(async () => {
      const maj = await api.requete<AnneeScolaire & { alignement: Alignement }>(
        `/school-years/${annee.id}/activate`,
        { methode: 'PUT' },
      );
      bilan = maj.alignement;
    });
    if (!ok) return;
    /*
     * Quatre rechargements, et non deux.
     *
     * La liste pour l'étiquette « active », le contexte parce que tous les
     * autres écrans en dépendent — et depuis que l'activation aligne le
     * découpage, les périodes et les réglages aussi. Sans eux l'écran
     * continuait d'afficher les dates d'avant, et l'on rouvrait la page en se
     * demandant si le bouton avait fait quelque chose.
     */
    await Promise.all([
      annees.recharger(), anneeActive.recharger(),
      periodes.recharger(), reglages.recharger(),
    ]);
    notifier(messageAlignement(annee.label, bilan));
  }


  async function ajouterSousDomaine(evenement: FormEvent) {
    evenement.preventDefault();
    if (!sousDomaines) return;
    const ok = await executer(() =>
      api.requete(`/subjects/${sousDomaines.id}/areas`, {
        methode: 'POST',
        corps: { name: nomSousDomaine.trim(), position: sousDomaines.areas.length },
      }));
    if (ok) {
      setNomSousDomaine('');
      const fraiches = await api.requete<Matiere[]>('/subjects');
      matieres.definir(fraiches);
      // La matière ouverte doit refléter l'ajout : garder l'ancienne
      // référence afficherait une liste périmée jusqu'à la réouverture.
      setSousDomaines(fraiches.find((m) => m.id === sousDomaines.id) ?? null);
    }
  }

  async function supprimerSousDomaine(id: string) {
    if (!sousDomaines) return;
    const ok = await executer(() =>
      api.requete(`/subject-areas/${id}`, { methode: 'DELETE' }));
    if (ok) {
      const fraiches = await api.requete<Matiere[]>('/subjects');
      matieres.definir(fraiches);
      setSousDomaines(fraiches.find((m) => m.id === sousDomaines.id) ?? null);
    }
  }

  return (
    <>
      <EnTetePage
        titre="Réglages"
        contexte={anneeActive.donnees?.label ?? 'Aucune année active'}
      />

      <div className="flex flex-col gap-4">
      {erreur && <Message ton="erreur">{erreur}</Message>}

      <BarreOutils>
        <GroupeBoutons
          legende="Section des réglages"
          valeur={onglet}
          surChangement={setOnglet}
          options={[
            { valeur: 'general', texte: 'Général' },
            { valeur: 'annee', texte: 'Année scolaire' },
            { valeur: 'periodes', texte: 'Périodes' },
            { valeur: 'matieres', texte: 'Matières & domaines' },
            { valeur: 'suppressions', texte: 'Suppressions' },
          ]}
        />
      </BarreOutils>

      {onglet === 'general' && (
        <>
          <ReglagesGeneral
            niveaux={niveauxChoisis}
            zone={zoneChoisie}
            nomEcole={moi.donnees?.tenantName ?? null}
            surNomEcole={async (nom) => {
              await api.requete('/tenant', { methode: 'PATCH', corps: { name: nom } });
              // Rechargé : le nom s'affiche aussi en haut de la barre latérale,
              // qui lit la même route. Le recopier ici laisserait les deux
              // divergents jusqu'au prochain rechargement de page.
              await moi.recharger();
            }}
            vacances={vacances.donnees?.holidays ?? null}
            chargementVacances={vacances.chargement}
            surNiveaux={(n) => void enregistrerNiveaux(n)}
            surZone={(z) => void enregistrerReglage('zone', z)}
            /*
             * Les niveaux appartiennent à la CLASSE, pas à l'école.
             *
             * Dans une école à plusieurs classes, un réglage d'établissement
             * n'a aucun moyen de distinguer un CE2 d'un CM1 : les deux se
             * verraient proposer le même programme. Ils sont donc portés par
             * la classe — et cet écran écrit dessus.
             *
             * Sans classe (compte de direction) ou sans droit de modification,
             * la carte se retire : proposer un réglage qui n'aboutit pas est
             * pire que de ne rien proposer.
             */
            classeCourante={classeCourante?.name ?? null}
            modifiable={peutModifierClasse}
          />
          {/*
            * Le PROGRAMME OFFICIEL ne se règle plus ici.
            *
            * Choisir l'édition du référentiel est un paramétrage de
            * plateforme, pas d'établissement : le catalogue est global,
            * partagé par toutes les écoles, et le figer relève de qui le
            * publie. Une école suit désormais la publication la plus récente,
            * sauf décision prise depuis le back-office.
            */}

        </>
      )}

      {onglet === 'annee' && (
      <Carte
        titre="Années scolaires"
        action={peutAdministrer ? (
          <Bouton
            variante="secondary"
            icone={<IconePlus taille={16} />}
            onClick={() => ouvrirAnnee(null)}
          >
            Nouvelle année
          </Bouton>
        ) : undefined}
      >
        {(annees.donnees?.length ?? 0) === 0 ? (
          <Vide titre="Aucune année scolaire">
            Tout (élèves, séances, progressions) se rattache à une année
            scolaire.{' '}
            {peutAdministrer
              ? 'Créez-en une pour commencer.'
              : 'C’est la direction de l’école qui l’ouvre.'}
          </Vide>
        ) : (
          // Tableau plutôt que liste : les dates et l'état se comparent d'une
          // ligne à l'autre, ce qu'une suite de `justify-between` interdit —
          // chaque bouton s'y plaçait à une abscisse différente selon la
          // longueur du libellé qui le précédait.
          <Tableau
            redimensionnable="annees"
            legende="Années scolaires enregistrées"
            lignes={annees.donnees ?? []}
            cleLigne={(a) => a.id}
            colonnes={[
              {
                cle: 'label',
                entete: 'Année',
                cellule: (a) => <span className="font-medium">{a.label}</span>,
              },
              {
                cle: 'periode',
                entete: 'Du … au',
                largeur: '16rem',
                cellule: (a) => (
                  <span className="text-[var(--color-fg-muted)]">
                    {`${dateFr(a.startsOn)} · ${dateFr(a.endsOn)}`}
                  </span>
                ),
              },
              {
                cle: 'etat',
                entete: 'État',
                actions: true,
                largeur: '1%',
                cellule: (a) => (
                  <ActionsLigne>
                    {a.active ? (
                      <Etiquette ton="succes">Année en cours</Etiquette>
                    ) : peutAdministrer ? (
                      <Bouton
                        variante="secondary"
                        taille="sm"
                        onClick={() => void activer(a)}
                        aria-label={`Activer l'année ${a.label}`}
                      >
                        Activer
                      </Bouton>
                    ) : (
                      <Etiquette>Année passée</Etiquette>
                    )}
                    {/*
                      * Corriger un libellé ou une date de rentrée saisie de
                      * travers. Sans ce geste, il fallait créer une seconde
                      * année et y tout reprendre.
                      */}
                    {peutAdministrer && aUneAnneeAvant(a) && (
                      <Bouton
                        variante="ghost"
                        taille="sm"
                        onClick={() => setRepriseVers({ id: a.id, label: a.label })}
                        aria-label={`Reprendre une année précédente dans ${a.label}`}
                      >
                        Reprendre…
                      </Bouton>
                    )}
                    {peutAdministrer && (
                      <Bouton
                        variante="ghost"
                        taille="sm"
                        iconeSeule
                        icone={<IconeCrayon taille={16} />}
                        onClick={() => ouvrirAnnee(a)}
                        aria-label={`Modifier l'année ${a.label}`}
                      >
                        Modifier
                      </Bouton>
                    )}
                  </ActionsLigne>
                ),
              },
            ]}
          />
        )}
      </Carte>

      )}

      {onglet === 'periodes' && (
      /*
       * ── Les périodes se LISENT ici, elles ne s'y modifient plus ──────────
       * Elles viennent du calendrier officiel, tenu au back-office, et sont
       * recopiées à l'ouverture de l'année. Les laisser modifiables école par
       * école produisait des découpages qui divergeaient du référentiel sans
       * que personne ne sache lequel faisait foi — et une progression calée
       * sur des dates fausses ne se rattrape pas.
       *
       * Une correction — arrêté rectificatif, école aux dates dérogatoires —
       * passe désormais par le support, qui refait le calendrier de l'école
       * depuis le back-office.
       *
       * Ce que l'école règle encore elle-même : les SEMAINES de chaque
       * période. Une semaine banalisée ou un stage lui appartiennent, et ne
       * regardent pas le calendrier national.
       */
      <Carte
        titre="Périodes de l'année en cours"
        description="Elles viennent du calendrier officiel et bornent les progressions, la programmation et les bilans."
      >
        {!idAnneeActive ? (
          <Message>Activez une année scolaire pour définir ses périodes.</Message>
        ) : (periodes.donnees?.length ?? 0) === 0 ? (
          <Vide titre="Aucune période définie">
            {/*
              * Dit ce qu'il faut FAIRE, et pourquoi il peut n'y avoir rien.
              *
              * Une année créée pour une rentrée que le calendrier officiel ne
              * couvre pas encore naît sans périodes — c'est délibéré, on
              * n'invente pas des dates de vacances. Mais « Aucune période
              * définie » n'expliquait pas pourquoi, et laissait chercher la
              * panne : les progressions n'ont alors aucune semaine et la
              * programmation aucune colonne.
              */}
            Les périodes viennent du calendrier officiel et sont créées avec
            l’année. Il n’y en a aucune ici : la rentrée choisie n’est pas
            encore publiée par arrêté, ou l’année a été ouverte avant qu’elle
            ne le soit. Écrivez-nous, nous les poserons, sans périodes, les
            progressions n’ont pas de semaines et la programmation pas de
            colonnes.
          </Vide>
        ) : (
          <Tableau
            redimensionnable="periodes"
            legende="Périodes de l'année scolaire en cours"
            lignes={periodes.donnees ?? []}
            cleLigne={(p) => p.id}
            colonnes={[
              {
                cle: 'numero',
                entete: 'N°',
                largeur: '3.5rem',
                cellule: (p) => (
                  <span className="text-[var(--color-fg-muted)]">{p.number}</span>
                ),
              },
              {
                cle: 'nom',
                entete: 'Période',
                cellule: (p) => <span className="font-medium">{p.name}</span>,
              },
              {
                cle: 'dates',
                entete: 'Du … au',
                largeur: '16rem',
                cellule: (p) => (
                  <span className="text-[var(--color-fg-muted)]">
                    {`${dateFr(p.startsOn)} · ${dateFr(p.endsOn)}`}
                  </span>
                ),
              },
              {
                cle: 'semaines',
                entete: 'Semaines',
                largeur: '9rem',
                /*
                 * ── Un NOMBRE, plus un geste ───────────────────────────────
                 * L'ajustement des semaines valait pour toute l'école, comme
                 * les périodes elles-mêmes : deux endroits pour un même
                 * découpage finissaient par en produire deux différents. Il se
                 * règle désormais au back-office.
                 *
                 * Le nombre reste : c'est ce qu'on vient vérifier avant de
                 * caler une progression.
                 */
                cellule: (p) => {
                  const nb = semainesDePeriode(p, semainesPerso).length;
                  const surMesure = Boolean(semainesPerso?.[p.id]?.length);
                  const texte = `${nb} semaines${surMesure ? ' (ajustées)' : ''}`;
                  return <span className="text-[var(--color-fg-muted)]">{texte}</span>;
                },
              },
            ]}
          />
        )}
      </Carte>

      )}

      {onglet === 'matieres' && (
        <ReglagesMatieres
          domaines={arbreProgramme.donnees ?? null}
          chargement={arbreProgramme.chargement}
          couleurs={Object.fromEntries(
            (matieres.donnees ?? []).map((m) => [m.name, m.color]),
          )}
          niveaux={niveauxChoisis.join(', ')}
          surCouleur={(nom, couleur) => void changerCouleurMatiere(nom, couleur)}
        />
      )}

      {onglet === 'suppressions' && (
        <ReglagesSuppressions
          enCours={enCours}
          surExport={() => void exporterEspace()}
          surSuppression={toutSupprimer}
          exportEcole={moi.donnees?.role === 'OWNER' || moi.donnees?.role === 'ADMIN'}
          /*
           * Les deux gestes sont réservés au PROPRIÉTAIRE, et le serveur
           * l'exige déjà. L'interface le disait nulle part : un enseignant
           * d'une école voyait « Effacer toutes vos données », cliquait, et
           * recevait un refus sans comprendre pourquoi.
           *
           * Dans une école, c'est la direction qui décide de fermer l'espace —
           * et qui retire un enseignant depuis l'écran Équipe, en le
           * choisissant. Pour un enseignant seul, propriétaire du sien, rien
           * ne change.
           */
          estProprietaire={moi.donnees?.role === 'OWNER'}
        />
      )}

      <DialogueReprise
        annee={repriseVers}
        surFermeture={() => setRepriseVers(null)}
        surTerminee={(message) => {
          setRepriseVers(null);
          notifier(message);
        }}
      />

      <Dialogue
        titre={anneeEditee ? `Modifier ${anneeEditee.label}` : 'Nouvelle année scolaire'}
        ouvert={anneeOuverte}
        surFermeture={() => { setAnneeOuverte(false); setAnneeEditee(null); }}
      >
        <form onSubmit={enregistrerAnnee} className="flex flex-col gap-4">
          {/*
            * ── On CHOISIT une rentrée, on ne saisit plus rien ──────────────
            * Le libellé et les deux dates viennent du calendrier officiel
            * tenu au back-office, pour la zone de l'école — en création comme
            * en modification. Les saisir à la main donnait des années au nom
            * faux ou décalées d'un an, dont plus aucune période ne
            * correspondait.
            */}
          <Selecteur
            libelle="Rentrée"
            value={rentreeChoisie}
            onChange={(e) => setRentreeChoisie(e.target.value)}
            options={[
              ...(rentreeChoisie === '' ? [{ valeur: '', texte: 'Choisir une rentrée…' }] : []),
              ...(anneeEditee ? (publiees.donnees ?? []) : ouvrables).map((c) => ({
                valeur: String(c.year),
                texte: c.label,
              })),
            ]}
            required
          />

          {!anneeEditee && publiees.donnees && ouvrables.length === 0 && (
            /*
             * Liste vide : toutes les rentrées publiées sont déjà ouvertes ici,
             * ou la suivante n'est pas encore saisie côté plateforme — seul le
             * support peut l'ajouter.
             */
            <Message ton="attention">
              Aucune rentrée à ouvrir : celles publiées sont déjà
              enregistrées ici. Pour une rentrée plus lointaine, le
              calendrier officiel n’est pas encore paru :{' '}
              <Link to="/support" className="underline">écrivez-nous</Link>{' '}
              et nous l’ajouterons.
            </Message>
          )}
          {anneeEditee && publiees.donnees && rentreeChoisie === '' && (
            <Message ton="attention">
              La rentrée de cette année n’est plus publiée au calendrier
              officiel. Choisissez celle qui lui correspond, ou{' '}
              <Link to="/support" className="underline">écrivez-nous</Link>.
            </Message>
          )}

          {/* Textes d'information sur les dates et les périodes retirés à la demande. */}
          {!anneeEditee && (
            <Message>
              Créer une année ne l'active pas : vous pourrez basculer dessus quand
              vous serez prêt.
            </Message>
          )}
          <ActionsDialogue>
            <Bouton
              variante="ghost"
              onClick={() => { setAnneeOuverte(false); setAnneeEditee(null); }}
            >
              Annuler
            </Bouton>
            <Bouton type="submit" chargement={enCours} disabled={rentreeChoisie === ''}>
              {anneeEditee ? 'Enregistrer' : 'Créer'}
            </Bouton>
          </ActionsDialogue>
        </form>
      </Dialogue>

      <Dialogue
        titre={`Sous-domaines · ${sousDomaines?.name ?? ''}`}
        ouvert={sousDomaines !== null}
        surFermeture={() => setSousDomaines(null)}
        // Liste modifiée au fil de l'eau, sans validation d'ensemble : la
        // croix est la seule sortie possible.
        fermetureLibre
      >
        <div className="flex flex-col gap-4">
          <Message>
            Les sous-domaines affinent une matière : « Numération » et
            « Géométrie » sous Mathématiques, par exemple.
          </Message>

          {(sousDomaines?.areas.length ?? 0) === 0 ? (
            <Message>Aucun sous-domaine pour cette matière.</Message>
          ) : (
            <ul className="flex flex-col gap-2">
              {sousDomaines?.areas.map((area) => (
                <li key={area.id} className="flex items-center justify-between gap-2 text-sm">
                  <span>{area.name}</span>
                  <Bouton
                    variante="ghost"
                    onClick={() => void supprimerSousDomaine(area.id)}
                    aria-label={`Supprimer le sous-domaine ${area.name}`}
                  >
                    Supprimer
                  </Bouton>
                </li>
              ))}
            </ul>
          )}

          <form onSubmit={ajouterSousDomaine} className="flex flex-col gap-2">
            <Champ
              libelle="Nouveau sous-domaine"
              value={nomSousDomaine}
              onChange={(e) => setNomSousDomaine(e.target.value)}
              autoFocus
            />
            <Bouton type="submit" chargement={enCours} disabled={!nomSousDomaine.trim()}>
              Ajouter
            </Bouton>
          </form>
        </div>
      </Dialogue>




      </div>
    </>
  );
}
